import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save, X, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import type { ClinicDetailDto } from '../types/clinic';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicInfoSection.css';

interface ClinicCategoriesSectionProps {
  clinic: ClinicDetailDto;
  onUpdate: () => void;
}

export default function ClinicCategoriesSection({ clinic, onUpdate }: Readonly<ClinicCategoriesSectionProps>) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);

  // Sync selected IDs with clinic data when clinic prop changes and not editing
  useEffect(() => {
    if (!isEditing) {
      setSelectedIds(clinic.categories?.map(c => c.id) ?? []);
    }
  }, [clinic, isEditing]);

  // Fetch all category types
  const { data: categoryTypes = [] } = useQuery({
    queryKey: ['categoryTypes'],
    queryFn: clinicApi.getCategoryTypes,
  });

  const updateMutation = useMutation({
    mutationFn: (categoryTypeIds: number[]) =>
      clinicApi.updateCategories(clinic.id, categoryTypeIds),
    onSuccess: () => {
      setIsEditing(false);
      setShowSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      onUpdate();
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(selectedIds);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedIds(clinic.categories?.map(c => c.id) ?? []);
    updateMutation.reset();
  };

  const handleEdit = () => {
    setSelectedIds(clinic.categories?.map(c => c.id) ?? []);
    updateMutation.reset();
    setIsEditing(true);
  };

  const toggleCategory = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  // Build display string for view mode
  const displayText = (() => {
    if (!clinic.categories || clinic.categories.length === 0) return 'None';
    return clinic.categories.map(c => c.name).join(', ');
  })();

  return (
    <div className="info-section">
      {/* Section header with edit/save/cancel */}
      <div className="info-section-header">
        <button
          type="button"
          className="info-section-toggle"
          onClick={() => !isEditing && setIsCollapsed(c => !c)}
          aria-expanded={!isCollapsed}
          disabled={isEditing}
        >
          <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
          <h3 className="info-section-title">Categories</h3>
        </button>
        <div className="info-section-actions">
          {showSuccess && (
            <span className="info-success-toast">
              <CheckCircle size={14} />
              Saved
            </span>
          )}
          {isEditing ? (
            <>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-cancel"
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                <X size={14} />
                Cancel
              </button>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-save"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                <Save size={14} />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <RequirePermission permission="UserCanEditClinicCategories">
              <button
                type="button"
                className="svc-edit-btn"
                onClick={handleEdit}
                title="Edit categories"
              >
                <Pencil size={14} />
                Edit
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <>
          {updateMutation.isError && (
            <div className="info-save-error">
              <AlertCircle size={14} />
              {updateMutation.error instanceof Error ? updateMutation.error.message : 'Failed to save changes'}
            </div>
          )}

          <div className="info-content">
            {isEditing ? (
              <div className="info-details info-details-checkbox-grid">
                {categoryTypes.map(type => (
                  <label key={type.id} className="info-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(type.id)}
                      onChange={() => toggleCategory(type.id)}
                    />
                    <span>{type.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="info-details">
                <div className="info-row">
                  <div className="info-field info-field-wide">
                    <label>Assigned Categories</label>
                    <span>{displayText}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
